import { Trans } from '@lingui/macro'
import { TextField } from '@mui/material'
import { PickersDay } from '@mui/x-date-pickers'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { dateFormat } from 'app/appSettings'
import moment from 'moment'
import { useRef, useState } from 'react'
import { FormLabel } from '../../common/labels/FormLabel'

/**
 * Extended Material UI DatePicker that handles portal localization and overriding theme color. Is recommended to use over regular <DatePicker /> component.
 * @category Common components
 * @subcategory MUI extended
 * @component
 * @returns {JSX.Element}
 * @property {color} color Focus color of the field
 * @property {function} handleClose Callback fired on inputs onClose event
 * @property {function} handleOpen Callback fired on inputs onOpen event
 */
const CustomDatePicker = ({ value, ...props }) => {
  const color = props.color
  const handleClose = props.handleClose
  const handleOpen = props.handleOpen
  const [dialogDateSelected, setDialogDateSelected] = useState()
  const placeholderValue = value ? moment.utc(value) : moment.utc()

  const [open, setOpen] = useState(false)
  const textFieldRef = useRef(null)
  const currentDateRef = useRef(null)

  const month = (props.minDate && moment.utc(value) < moment.utc(props.minDate))
    ? moment.utc(props.minDate)
    : (props.maxDate && (moment.utc(value) > moment.utc(props.maxDate)))
        ? moment.utc(props.maxDate)
        : value ? moment.utc(value) : moment.utc()

  const onOpen = () => {
    currentDateRef.current = value
    setOpen(true)
    setTimeout(() => {
      if (textFieldRef.current && !props.label) {
        const textInput = textFieldRef.current.querySelector('input')
        if (currentDateRef.current) {
          textInput.value = currentDateRef.current.format ? currentDateRef.current.format(dateFormat) : currentDateRef.current
        }
      }
    }, 0)
    if (handleOpen) {
      handleOpen()
    }
  }

  return (
    <>
      <DatePicker
        {...props}
        open={open}
        value={open
          ? null
          : value ? moment.utc(value) : placeholderValue}
        inputFormat={dateFormat}
        defaultCalendarMonth={month}
        renderInput={(props) => {
          return (
            <TextField
              {...props}
              ref={textFieldRef}
              inputProps={{
                ...props.inputProps,
                value: value ? props.inputProps.value : '',
                placeholder: '',
                readOnly: true
              }}
              onClick={() => onOpen()}
              sx={{
                '& .MuiInputBase-input': {
                  lineHeight: '20px !important',
                  color: 'rgba(0, 0, 0, 0.87)',
                  paddingTop: '10px',
                  paddingBottom: '10px',
                  paddingLeft: '12px'
                },
                '& .MuiOutlinedInput-root': {
                  lineHeight: '20px !important',
                  color: 'rgba(0, 0, 0, 0.87)',
                  paddingTop: '10px',
                  paddingBottom: '10px',
                  paddingLeft: '12px',
                  paddingRight: '10px',
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.40)'
                  },
                  '&:hover fieldset': {
                    borderColor: '#17374F'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#000'
                  }
                },
                '& .MuiInputAdornment-root': {
                  marginRight: '10px'
                }
              }}
              fullWidth
              variant='outlined'
            />
          )
        }}
        minDate={props.minDate || undefined}
        maxDate={props.maxDate || undefined}
        onOpen={() => onOpen()}
        dayOfWeekFormatter={(day) => day}
        onClose={() => {
          setOpen(false)
          if (handleClose) {
            handleClose(dialogDateSelected)
          }
        }}
        renderDay={(day, selectedDay, dayProps) => {
          const isSelected = day.isSame(selectedDay, 'day')
          const isValue = day.isSame(moment.utc(currentDateRef.current), 'day')
          const isDisabled = dayProps.disabled
          return (
            <PickersDay
              day={day}
              {...dayProps}
              onClick={(e) => {
                setDialogDateSelected(day)
              }}
              sx={{
                backgroundColor: isDisabled
                  ? '#eeefe6'
                  : isSelected
                    ? 'transparent'
                    : isValue
                      ? '#17374F'
                      : 'white',
                borderRadius: '50%',
                ...(isValue && {
                  color: 'white'
                })
              }}
            />
          )
        }}
        // ToolbarComponent={t => {
        //   const { date, setOpenView } = t
        //   let dateString = date.locale(userLang).format('dddd, D MMMM')
        //   if (userLang === 'fr') {
        //     const month =
        //       date.format('MMMM')[0].toLowerCase() +
        //       date.format('MMMM').slice(1)
        //     const day = date.format('D')
        //     const weekday =
        //       date.format('dddd')[0].toUpperCase() +
        //       date.format('dddd').slice(1)
        //     dateString = weekday + ' ' + day + ' ' + month
        //   }
        //   return (
        //     <div
        //       style={{
        //         padding: 24,
        //         height: 100
        //         // backgroundColor: theme.palette.primary.main
        //       }}
        //     >
        //       <div>
        //         <Button
        //           onClick={e => {
        //             setOpenView('year')
        //           }}
        //           style={{
        //             padding: 0,
        //             justifyContent: 'flex-start',
        //             textAlign: 'left'
        //           }}
        //         >
        //           <Typography style={{ fontSize: 16 }}>
        //             {moment(date).year()}
        //           </Typography>
        //         </Button>
        //       </div>
        //       <div>
        //         <Button
        //           style={{ padding: 0 }}
        //           onClick={e => {
        //             setOpenView('date')
        //           }}
        //         >
        //           <Typography variant='h4'>{dateString}</Typography>
        //         </Button>
        //       </div>
        //     </div>
        //   )
        // }}
      />
      {props.error && props.metaError && (
        <FormLabel error label={<Trans id={props.metaError?.props?.id} />} />
      )}
    </>
  )
}

export default CustomDatePicker
